import { isEqual } from "lodash";

import { PanelState, State, Layout } from "@/shared/state/visualization";

export interface VizStateChangeSet {
  panels: {
    /**
     * Panels the current state has and the initial state does not.
     */
    added: Record<string, PanelState>;

    /**
     * Panels the initial state has and the current state does not, as they were
     * in the initial state.
     */
    removed: Record<string, PanelState>;

    /**
     * Panels both states have that differ in some field other than `initParams`,
     * in their current form. `initParams` is left out of the comparison so that
     * panning or zooming a panel does not register as an edit worth saving.
     */
    changed: Record<string, PanelState>;
  };
  layout: {
    changed: Layout | null;
  };
  hasChanges: boolean;
}

/**
 * Compares a workspace snapshot against the workspace as it currently stands,
 * driving both the unsaved-changes prompt and the rewrite of an already-saved
 * layout.
 *
 * Panels sort into the added, removed, and changed buckets described on
 * `VizStateChangeSet`; a state with no `panels` counts as having none. The layout
 * is compared whole, so any difference at all reports the current layout under
 * `layout.changed`, and an identical layout reports `null`.
 */
export function diffVizState(
  initialState: Pick<State, "panels" | "layout">,
  currentState: Pick<State, "panels" | "layout">,
): VizStateChangeSet {
  const initialPanels = initialState.panels ?? {};
  const currentPanels = currentState.panels ?? {};

  const removed: Record<string, PanelState> = {};
  const added: Record<string, PanelState> = {};
  const changed: Record<string, PanelState> = {};

  Object.entries(initialPanels).forEach(([id, panel]) => {
    if (!currentPanels[id]) {
      removed[id] = panel;
    }
  });

  Object.entries(currentPanels).forEach(([id, currentPanel]) => {
    const initialPanel = initialPanels[id];

    if (!initialPanel) {
      added[id] = currentPanel;
    } else {
      const { initParams: _a, ...initialParams } = initialPanel;
      const { initParams: _b, ...currentParams } = currentPanel;
      if (!isEqual(initialParams, currentParams)) {
        changed[id] = currentPanel;
      }
    }
  });

  const initialLayout = initialState.layout;
  const currentLayout = currentState.layout;

  const hasLayoutChanged = !isEqual(initialLayout, currentLayout);

  const hasChanges =
    hasLayoutChanged ||
    Object.keys(removed).length > 0 ||
    Object.keys(added).length > 0 ||
    Object.keys(changed).length > 0;

  return {
    panels: {
      removed,
      added,
      changed,
    },
    layout: {
      changed: hasLayoutChanged ? currentLayout : null,
    },
    hasChanges,
  };
}
